import React from 'react';
import { IMFormModal, IMSelect, IMSwissSelect, OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import SubtitleWithLines from 'components/common/general/SubtitleWithLines';
import { observer } from 'mobx-react-lite';
import { useBiDashboardPageStore } from 'store/pages/bi-groups-dashboard/pageStore';
import { MOVE_GROUP_MODAL_ID } from 'components/bi-dashboard/constants';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import {
  CurrentAffiliationBlockStyled,
  FormWrapperStyled,
  DisabledSelectStyled,
  StyledGroupName,
  SubtitleWithLinesStyled,
  StepBlockStyled,
  StepHeaderStyled,
  StepNumberStyled,
  StepHeaderTextStyled,
  TargetAgencyBlockStyled,
  AorBlockStyled,
  NoAorsAvailableMessage,
} from './styles';

const MoveGroupModal: React.FC = observer(() => {
  const {
    groupInteractionDelegate: {
      moveModal,
      selectedGroup,
      moveGroupModalUiStore: { searchAgencyDelegate, form, isAorSectionLoading, agencyProducers, resetModalData },
    },
  } = useBiDashboardPageStore();

  const agencyOptions: OptionType[] = searchAgencyDelegate.allSponsorAgencies.map((sponsorAgency) => ({
    label: sponsorAgency.name,
    value: sponsorAgency.id,
  }));

  const onClose = (): void => {
    resetModalData();
    moveModal.close();
  };

  return (
    <IMFormModal
      show={moveModal.isVisible}
      title="Move Group"
      subtitle={
        <>
          You are about to move <StyledGroupName>{selectedGroup.name}</StyledGroupName> group.
          <br /> Please choose the target agency from the list below.
        </>
      }
      closeButtonText="Cancel"
      submitButtonText="Move group"
      onClose={onClose}
      modalType={ModalType.NOTIFICATION}
      formController={form}
      formId={MOVE_GROUP_MODAL_ID}
      formContent={
        <FormWrapperStyled>
          <SubtitleWithLines text="Current Group Affiliation" />
          <CurrentAffiliationBlockStyled>
            <DisabledSelectStyled
              name="currentAgency"
              label="Current agency"
              options={agencyOptions}
              value={selectedGroup.agencyId}
              placeholder="Select agency"
              disabled
              width="100%"
            />
            <DisabledSelectStyled
              name="currentAor"
              label="Agent of Record"
              options={[]}
              value=""
              placeholder={selectedGroup.aor?.name || 'Agent of Record is Missing'}
              disabled
              width="100%"
            />
          </CurrentAffiliationBlockStyled>

          <SubtitleWithLinesStyled text="Updated Group Affiliation" />
          <StepBlockStyled>
            <StepHeaderStyled>
              <StepNumberStyled>1</StepNumberStyled>
              <StepHeaderTextStyled>Select Target Agency</StepHeaderTextStyled>
            </StepHeaderStyled>
            <TargetAgencyBlockStyled>
              <IMSwissSelect
                name="agencyId"
                label="Target agency"
                options={agencyOptions}
                placeholder="Select agency"
                size="full"
                withSearch
                searchPlaceholder="Agency name"
              />
            </TargetAgencyBlockStyled>
          </StepBlockStyled>

          <StepBlockStyled>
            <OverlaySpinner display={isAorSectionLoading} size="5rem" />
            <StepHeaderStyled>
              <StepNumberStyled>2</StepNumberStyled>
              <StepHeaderTextStyled>Select Agent of Record</StepHeaderTextStyled>
            </StepHeaderStyled>
            {agencyProducers?.length < 1 ? (
              <NoAorsAvailableMessage>
                There are currently no available Agent of Records for this agency.
              </NoAorsAvailableMessage>
            ) : (
              <AorBlockStyled>
                <IMSelect
                  name="aorId"
                  label="Agent of Record"
                  options={agencyProducers?.map((producer) => ({ label: producer.name, value: producer.id })) || []}
                  placeholder="Select agent of record"
                  size="full"
                />
              </AorBlockStyled>
            )}
          </StepBlockStyled>
        </FormWrapperStyled>
      }
    />
  );
});

export default MoveGroupModal;
